import {
  Outlet,
  createFileRoute,
  notFound,
  redirect,
  retainSearchParams,
} from "@tanstack/react-router";
import { z } from "zod";

import { apiErrorStatus, isApiError } from "@/core/lib/api-errors";
import { ProjectPageProvider } from "@/features/project/components/pages/project-page-context";
import { resolveAuditActive } from "@/features/project/components/pages/project-page-context-hooks";
import { projectSubtreeHref } from "@/features/project/lib/project-subtree-href";
import { getProjectHierarchyOptions } from "@/openapi/@tanstack/react-query.gen";

const searchSchema = z.object({
  entityProjectId: z.string().optional(),
  // agbw's new-run flag must survive the canonicalizing redirects below
  newVersion: z.boolean().optional(),
});

export const Route = createFileRoute("/_protected/auditor/clients/$id")({
  component: AuditProjectRoute,
  validateSearch: searchSchema,
  search: {
    // every navigation inside the client subtree keeps the selected dossier
    // without call sites having to thread it; explicit values (incl. an
    // explicit undefined) still win
    middlewares: [retainSearchParams(["entityProjectId"])],
  },
  beforeLoad: async ({
    context: { queryClient, ...context },
    params,
    search,
    location,
  }) => {
    const organizationId = context.user.organization?.id;
    // keep fetching in the beforeLoad so it's a bit easier to do redirects in child routes
    if (!organizationId) {
      throw redirect({ to: "/no-organization", throw: true });
    }

    try {
      const hierarchy = await queryClient.query({
        ...getProjectHierarchyOptions({
          path: { org_id: organizationId, project_id: params.id },
        }),
        staleTime: Infinity,
      });

      // canonical url is the client root; dossier/subsidiary links redirect
      // to the same sub-path and carry the requested node along as the
      // active entity
      if (hierarchy.root.id !== params.id) {
        throw redirect({
          href: projectSubtreeHref(
            location,
            hierarchy.root.id,
            params.id,
            search.entityProjectId ?? params.id,
          ),
          throw: true,
        });
      }

      // heal stale or foreign entity ids in shared/bookmarked urls
      if (
        search.entityProjectId !== undefined &&
        !hierarchy.children.some((child) => child.id === search.entityProjectId)
      ) {
        throw redirect({
          href: projectSubtreeHref(
            location,
            hierarchy.root.id,
            params.id,
            undefined,
          ),
          replace: true,
          throw: true,
        });
      }

      // the active dossier, resolved once here so every guarded child route
      // gates on the same project the pages themselves render
      return {
        hierarchy,
        active: resolveAuditActive(
          hierarchy.root,
          hierarchy.children,
          search.entityProjectId,
        ),
      };
    } catch (error) {
      if (
        isApiError(error) &&
        (apiErrorStatus(error) === 404 || apiErrorStatus(error) === 403)
      ) {
        throw notFound();
      }
      throw error;
    }
  },
});

function AuditProjectRoute() {
  const { id } = Route.useParams();

  return (
    <ProjectPageProvider value={{ module: "audit", projectId: id }}>
      <Outlet />
    </ProjectPageProvider>
  );
}
